import type { WriteTransaction } from 'replicache'
import {
  SuggestedBranch,
  branchBaseOps,
  suggestedBranchBaseOps,
} from '@basehub/data/replicache/index'
import { WithServerSecret } from '../../../tx/spec'
import { MutatorUserError } from '@basehub/data/replicache/error-handling'
import { hasAccessOrThrow } from '../../team-space/team/auth'
import { ensureBaseHubTransaction } from '@basehub/data/replicache/tx/iso'

export async function resolveSuggestedBranch(
  tx: WriteTransaction,
  {
    id,
    resolution,
    resolvedAt,
  }: WithServerSecret<{
    id: string
    resolution: SuggestedBranch['resolution']
    resolvedAt: string
  }>
) {
  ensureBaseHubTransaction(tx)
  hasAccessOrThrow({
    allowedRoles: ['editor', 'member', 'owner', 'viewer'],
    guestAccess: false,
    serverOnly: tx.serverOnly,
    clientOnly: tx.clientOnly,
    serverSecret: tx.serverSecret,
  })

  return await suggestedBranchBaseOps.update(tx, { id, resolution, resolvedAt })
}
export async function deleteSuggestedBranch(
  tx: WriteTransaction,
  { id }: WithServerSecret<{ id: string }>
) {
  ensureBaseHubTransaction(tx)

  hasAccessOrThrow({
    allowedRoles: ['editor', 'member', 'owner', 'viewer'],
    guestAccess: false,
    serverOnly: tx.serverOnly,
    clientOnly: tx.clientOnly,
    serverSecret: tx.serverSecret,
  })

  return await suggestedBranchBaseOps.delete(tx, id)
}

export async function deleteSuggestedBranchByName(
  tx: WriteTransaction,
  { name }: WithServerSecret<{ name: string }>
) {
  ensureBaseHubTransaction(tx)

  hasAccessOrThrow({
    allowedRoles: ['editor', 'member', 'owner', 'viewer'],
    guestAccess: false,
    serverOnly: tx.serverOnly,
    clientOnly: tx.clientOnly,
    serverSecret: tx.serverSecret,
  })

  const suggestedBranches = await suggestedBranchBaseOps.list(tx)
  const suggestedBranchesForThisName = suggestedBranches.find(
    (b) => b.name === name
  )

  if (!suggestedBranchesForThisName) return

  return await suggestedBranchBaseOps.delete(
    tx,
    suggestedBranchesForThisName.id
  )
}

export async function upsertSuggestedBranch(
  tx: WriteTransaction,
  {
    data,
  }: WithServerSecret<{
    data: Pick<SuggestedBranch, 'id' | 'createdAt' | 'git' | 'name'>
  }>
) {
  ensureBaseHubTransaction(tx)

  const [branches, suggestedBranches] = await Promise.all([
    branchBaseOps.list(tx),
    suggestedBranchBaseOps.list(tx),
  ])

  const existing = suggestedBranches.find(
    (b) => b.git?.branch === data.git?.branch
  )

  if (branches.some((b) => b.git?.branch === data.git?.branch)) {
    throw new MutatorUserError(
      'A branch linked to this git branch already exists'
    )
  }

  const suggestedBranch: SuggestedBranch = { ...existing, ...data }

  if (!existing) {
    await suggestedBranchBaseOps.put(tx, suggestedBranch)
  } else {
    await suggestedBranchBaseOps.update(tx, suggestedBranch)
  }

  return suggestedBranch
}

export const suggestedBranchMutators = {
  getSuggestedBranch: suggestedBranchBaseOps.get,
  listSuggestedBranches: suggestedBranchBaseOps.list,
  deleteSuggestedBranch,
  deleteSuggestedBranchByName,
  resolveSuggestedBranch,
  upsertSuggestedBranch,
}
